import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  Fade,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCourses } from '../store/thunks/coursesThunks';
import { fetchLeaderboardEntries, getTopPerformers } from '../services/firestore/leaderboardService';
import { LeaderboardEntry } from '@e-learning/shared/src/store/types';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const LeaderboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { courses } = useAppSelector((state) => state.courses);
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [topPerformers, setTopPerformers] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<'beginner' | 'intermediate' | 'advanced' | 'mastery' | 'all'>('all');

  useEffect(() => {
    dispatch(fetchCourses());
  }, [dispatch]);

  useEffect(() => {
    loadLeaderboard();
  }, [selectedCategory, selectedDifficulty]);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const category = selectedCategory === 'all' ? undefined : selectedCategory;
      const difficulty = selectedDifficulty === 'all' ? undefined : selectedDifficulty;
      
      const [allEntries, top] = await Promise.all([
        fetchLeaderboardEntries(category, difficulty),
        getTopPerformers(10, category, difficulty),
      ]);
      
      setEntries(allEntries);
      setTopPerformers(top);
    } catch (err: any) {
      setError(err.message || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const categories = Array.from(new Set(courses.map((c) => c.category))).filter(Boolean);

  const getMedalColor = (position: number) => {
    if (position === 1) return '#FFD700'; // Gold
    if (position === 2) return '#C0C0C0'; // Silver
    if (position === 3) return '#CD7F32'; // Bronze
    return 'primary.main';
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Fade in timeout={600}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 193, 7, 0.2) 100%)',
              mb: 3,
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 60, color: 'warning.main' }} />
          </Box>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Leaderboard
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
            See how you rank against other learners. Compete and climb to the top!
          </Typography>
        </Box>
      </Fade>

      {/* Filters */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Difficulty</InputLabel>
            <Select
              value={selectedDifficulty}
              label="Difficulty"
              onChange={(e) => setSelectedDifficulty(e.target.value as any)}
            >
              <MenuItem value="all">All Levels</MenuItem>
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
              <MenuItem value="mastery">Mastery</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Fade>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Top Performers */}
          {topPerformers.length > 0 && (
            <Fade in timeout={1000}>
              <Box sx={{ mb: 6 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon />
                  Top Performers
                </Typography>
                <Grid container spacing={3}>
                  {topPerformers.slice(0, 3).map((entry, index) => (
                    <Grid item xs={12} md={4} key={`${entry.userId}-${entry.courseId}-${index}`}>
                      <Card
                        sx={{
                          background: `linear-gradient(135deg, rgba(${
                            index === 0 ? '255, 215, 0' : index === 1 ? '192, 192, 192' : '205, 127, 50'
                          }, 0.1) 0%, rgba(30, 41, 59, 0.9) 100%)`,
                          border: `2px solid rgba(${
                            index === 0 ? '255, 215, 0' : index === 1 ? '192, 192, 192' : '205, 127, 50'
                          }, 0.3)`,
                          textAlign: 'center',
                          p: 3,
                        }}
                      >
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <Avatar
                            sx={{
                              width: 80,
                              height: 80,
                              mx: 'auto',
                              bgcolor: getMedalColor(index + 1),
                              fontSize: '2rem',
                              fontWeight: 700,
                            }}
                          >
                            {index + 1}
                          </Avatar>
                          {index < 3 && (
                            <EmojiEventsIcon
                              sx={{
                                position: 'absolute',
                                top: -10,
                                right: '50%',
                                transform: 'translateX(50%)',
                                fontSize: 40,
                                color: getMedalColor(index + 1),
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {entry.userName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {entry.courseTitle}
                        </Typography>
                        <Chip
                          label={`${entry.score}%`}
                          color="primary"
                          sx={{ fontWeight: 600, fontSize: '1rem', py: 2.5 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {entry.correctCount}/{entry.totalQuestions} correct
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Fade>
          )}

          {/* Full Leaderboard Table */}
          <Fade in timeout={1200}>
            <Box>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Full Leaderboard
              </Typography>
              <TableContainer component={Paper} sx={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(148, 163, 184, 0.1)' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Difficulty</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Score</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Correct</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.slice(0, 100).map((entry, index) => (
                      <TableRow
                        key={`${entry.userId}-${entry.courseId}-${entry.timestamp}`}
                        sx={{
                          '&:hover': {
                            background: 'rgba(99, 102, 241, 0.1)',
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {index < 3 && (
                              <EmojiEventsIcon sx={{ fontSize: 20, color: getMedalColor(index + 1) }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: index < 3 ? 600 : 400 }}>
                              {index + 1}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                              {entry.userName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2">{entry.userName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{entry.courseTitle}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={entry.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.difficulty}
                            size="small"
                            color={
                              entry.difficulty === 'beginner'
                                ? 'success'
                                : entry.difficulty === 'intermediate'
                                ? 'info'
                                : entry.difficulty === 'advanced'
                                ? 'warning'
                                : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {entry.score}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {entry.correctCount}/{entry.totalQuestions}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {entries.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No leaderboard entries found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Take quizzes to appear on the leaderboard!
                  </Typography>
                </Box>
              )}
            </Box>
          </Fade>
        </>
      )}
    </Container>
  );
};

export default LeaderboardPage;

